import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import BlankEditor from "./BlankEditor";
import BlankSheetPreview from "./BlankSheetPreview";
import SignaturePad from "./SignaturePad";
import { draftFrom, payloadOf } from "./blankDraft";
import {
    LAYOUT_OPTIONS,
    ORDER_KINDS,
    STATUS_META,
    createDocumentPdf,
    dateText,
    deleteDocumentPdf,
    fetchDocumentPdfBlob,
    fetchDocumentPreview,
    fetchOrderDocument,
    fetchShotThumbBlob,
    fetchSignatureBlob,
    getApiError,
    getBlobApiError,
    openBlob,
    openInNewTab,
    revokeSignature,
    saveOrderDocument,
    signDocument,
    uploadPaperSignature,
} from "./orderDocumentApi";

import "../../../scss/adminOffice/components/order-document-AO.scss";

const MAX_SHOTS = 4;

function ShotThumb({ officeId, shot, selected, disabled, onToggle }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let url = "";
        let alive = true;
        fetchShotThumbBlob(officeId, shot.sessionCode, shot.id)
            .then((blob) => {
                if (!alive) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [officeId, shot.sessionCode, shot.id]);

    return (
        <button
            type="button"
            className={`order-doc-shot-AO ${selected ? "is-selected" : ""}`}
            disabled={disabled && !selected}
            onClick={onToggle}
            title={selected ? "Убрать из бланка" : "Добавить в бланк"}
        >
            {src ? <img src={src} alt={shot.title} /> : <span>Загрузка…</span>}
            <i>{selected ? "✓" : ""}</i>
            <small>{shot.title}</small>
        </button>
    );
}

function SignatureImage({ officeId, orderId, signature, version }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let url = "";
        let alive = true;
        if (!signature?.hasFile || signature.mimeType === "application/pdf") return undefined;
        fetchSignatureBlob(officeId, orderId)
            .then((blob) => {
                if (!alive) return;
                url = URL.createObjectURL(blob);
                setSrc(url);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [officeId, orderId, signature?.hasFile, signature?.mimeType, version]);

    if (!signature?.hasFile) return null;
    if (signature.mimeType === "application/pdf") {
        return (
            <button
                type="button"
                className="order-doc-AO__ghost"
                onClick={() => openInNewTab(() => fetchSignatureBlob(officeId, orderId), "signed-blank.pdf").catch(() => undefined)}
            >
                Открыть скан бланка (PDF)
            </button>
        );
    }
    return src ? <img className={`order-doc-AO__signature-img ${signature.method === "paper" ? "is-paper" : ""}`} src={src} alt="Подпись заказчика" /> : null;
}

/**
 * Документ заказа: бланк собирается из заказа сам. Здесь — что дописать
 * (усопший, срок, предоплата, условия), какие блоки печатать, снимки 3D-сцены,
 * PDF в двух экземплярах и подпись заказчика (на экране или фото бланка).
 */
export default function OrderDocumentPanel({ officeId, orderId, compact = false, onClose = null }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [section, setSection] = useState("blank");
    const [agreed, setAgreed] = useState(false);
    const [signerName, setSignerName] = useState("");
    const [padEmpty, setPadEmpty] = useState(true);
    const padRef = useRef(null);
    const paperRef = useRef(null);

    const apply = useCallback((result, message = "") => {
        setData(result);
        setDraft(draftFrom(result.form, result.document));
        setDirty(false);
        setSignerName((value) => value || result.document?.customer?.name || "");
        if (message || result.message) setNotice(message || result.message);
    }, []);

    const load = useCallback(async () => {
        if (!officeId || !orderId) return;
        setLoading(true);
        setError("");
        try {
            apply(await fetchOrderDocument(officeId, orderId));
        } catch (requestError) {
            setError(getApiError(requestError, "Не удалось загрузить документ заказа"));
        } finally {
            setLoading(false);
        }
    }, [officeId, orderId, apply]);

    useEffect(() => {
        load();
    }, [load]);

    const run = async (key, job, message = "") => {
        if (busy) return null;
        setBusy(key);
        setError("");
        setNotice("");
        try {
            const result = await job();
            if (result?.document) apply(result, message);
            return result;
        } catch (requestError) {
            setError(getApiError(requestError));
            return null;
        } finally {
            setBusy("");
        }
    };

    const patch = (group, key, value) => {
        setDraft((prev) => (group ? { ...prev, [group]: { ...prev[group], [key]: value } } : { ...prev, [key]: value }));
        setDirty(true);
        setNotice("");
    };

    const patchBlank = (updater) => {
        setDraft((prev) => ({ ...prev, blank: updater(prev.blank), blankTouched: true }));
        setDirty(true);
        setNotice("");
    };

    const save = () => run("save", () => saveOrderDocument(officeId, orderId, payloadOf(draft, true, data?.document?.items)), "Сохранено");

    /** Настоящий PDF по черновику (без сохранения) — в новой вкладке. */
    const openDraftPdf = () => openInNewTab(
        () => fetchDocumentPreview(officeId, orderId, payloadOf(draft, true, data?.document?.items)),
        `blank-${data?.document?.order?.number || "order"}-preview.pdf`
    ).catch(async (requestError) => setError(await getBlobApiError(requestError)));

    const makePdf = async () => {
        if (dirty) {
            const saved = await save();
            if (!saved) return;
        }
        // Вкладка открывается сразу, PDF появится в ней, когда сервер его соберёт.
        await openInNewTab(async () => {
            const result = await run("pdf", () => createDocumentPdf(officeId, orderId));
            if (!result?.pdf?.pdfId) return null;
            return fetchDocumentPdfBlob(officeId, orderId, result.pdf.pdfId);
        }, `blank-${data?.document?.order?.number || "order"}.pdf`).catch((requestError) => setError(getApiError(requestError)));
    };

    const sign = async () => {
        if (dirty) {
            const saved = await save();
            if (!saved) return;
        }
        const signature = padRef.current?.toDataUrl() || "";
        if (!signature) {
            setError("Заказчик ещё не расписался.");
            return;
        }
        await openInNewTab(async () => {
            const result = await run("sign", () => signDocument(officeId, orderId, { signerName, agreed, signature }));
            if (!result?.document) return null;
            padRef.current?.clear();
            setAgreed(false);
            return result.pdf?.pdfId ? fetchDocumentPdfBlob(officeId, orderId, result.pdf.pdfId) : null;
        }, `blank-${data?.document?.order?.number || "order"}-signed.pdf`).catch((requestError) => setError(getApiError(requestError)));
    };

    const doc = data?.document;
    const layout = useMemo(() => ({ ...(doc?.layout || {}), ...(draft?.layout || {}) }), [doc, draft]);

    // Бланк, начатый в 3D-калькуляторе этой сессии (вкладка «Печать формы»), — предложить подставить,
    // пока у заказа свой бланк ещё не сохраняли.
    const calcDraft = useMemo(() => {
        const code = String(doc?.order?.salesSessionCode || "").toUpperCase();
        const savedBlank = data?.form?.blank && typeof data.form.blank === "object" ? data.form.blank : {};
        if (!code || Object.keys(savedBlank).length) return null;
        try {
            const raw = window.localStorage.getItem(`alotb_blank_draft:${officeId}:${code}`);
            return raw ? JSON.parse(raw)?.draft || null : null;
        } catch (_error) {
            return null;
        }
    }, [doc, data, officeId]);
    const applyCalcDraft = () => {
        if (!calcDraft) return;
        setDraft((prev) => ({
            ...prev,
            // Автоснимки калькулятора живут только в браузере — в заказ ставим фото, выбранное в заказе.
            blank: {
                ...prev.blank,
                ...calcDraft.blank,
                front: { ...prev.blank.front, ...(calcDraft.blank?.front || {}), ...(calcDraft.blank?.front?.image?.type === "scene" ? { image: prev.blank.front.image } : {}) },
                back: { ...prev.blank.back, ...(calcDraft.blank?.back || {}), ...(calcDraft.blank?.back?.image?.type === "scene" ? { image: prev.blank.back.image } : {}) },
            },
            deceased: { ...prev.deceased, ...(calcDraft.deceased || {}) },
            blankTouched: true,
        }));
        setDirty(true);
        setNotice("Бланк из калькулятора подставлен — проверьте и сохраните");
    };

    if (loading && !data) return <div className="order-doc-AO__state">Собираем документ из заказа…</div>;
    if (!data || !doc) return <div className="order-doc-AO__state is-error">{error || "Документ не загрузился"}</div>;

    const status = STATUS_META[doc.status] || STATUS_META.not_signed;
    const canSign = !padEmpty && agreed && signerName.trim();
    const field = (group, key, label, placeholder = "", { multiline = false, rows = 2, type = "text" } = {}) => (
        <label className={multiline ? "is-wide" : ""}>
            <span>{label}</span>
            {multiline ? (
                <textarea rows={rows} value={draft[group][key]} placeholder={placeholder} onChange={(event) => patch(group, key, event.target.value)} />
            ) : (
                <input type={type} value={draft[group][key]} placeholder={placeholder} onChange={(event) => patch(group, key, event.target.value)} />
            )}
        </label>
    );

    return (
        <div className={`order-doc-AO ${compact ? "is-compact" : ""}`}>
            <div className="order-doc-AO__top">
                <div>
                    <span className={`order-doc-AO__status is-${status.tone}`}>{status.label}</span>
                    <b>Бланк заказа № {doc.order.number}</b>
                    <small>Версия {doc.version} · {doc.order.status}</small>
                </div>
                <div className="order-doc-AO__top-actions">
                    {dirty ? (
                        <button type="button" className="order-doc-AO__ghost" disabled={!!busy} onClick={save}>
                            {busy === "save" ? "Сохраняем…" : "Сохранить правки"}
                        </button>
                    ) : null}
                    <button type="button" className="order-doc-AO__primary" disabled={!!busy} onClick={makePdf}>
                        {busy === "pdf" ? "Готовим PDF…" : "PDF · 2 экземпляра"}
                    </button>
                    {onClose ? (
                        <button type="button" className="order-doc-AO__close" title="Закрыть" onClick={onClose}>×</button>
                    ) : null}
                </div>
            </div>

            {calcDraft && !dirty ? (
                <div className="order-doc-AO__notice is-info">
                    Для этой 3D-сессии в калькуляторе уже заполняли бланк (вкладка «Печать формы»).{" "}
                    <button type="button" className="order-doc-AO__link" onClick={applyCalcDraft}>Подставить его</button>
                </div>
            ) : null}
            {doc.changedAfterSign ? (
                <div className="order-doc-AO__notice is-warn">
                    Заказ или бланк изменились после подписи {dateText(doc.signature?.signedAt, true)}. Сформируйте новый бланк и попросите заказчика подписать его ещё раз.
                </div>
            ) : null}
            {notice ? <div className="order-doc-AO__notice">{notice}</div> : null}
            {error ? <div className="order-doc-AO__notice is-error">{error}</div> : null}

            <div className="order-doc-AO__tabs">
                {[
                    ["blank", `Бланк${dirty ? " •" : ""}`],
                    ["edit", "Ещё"],
                    ["sign", doc.status === "signed" ? "Подпись ✓" : "Подпись"],
                    ["files", `PDF${data.pdfs.length ? ` · ${data.pdfs.length}` : ""}`],
                ].map(([key, label]) => (
                    <button type="button" key={key} className={section === key ? "is-active" : ""} onClick={() => setSection(key)}>{label}</button>
                ))}
            </div>

            {section === "blank" && draft ? (
                <div className="order-blank-AO__layout">
                    <div className="order-blank-AO__form">
                        <BlankEditor
                            officeId={officeId}
                            doc={doc}
                            draft={draft}
                            onBlank={patchBlank}
                            onField={patch}
                        />
                        <div className="order-doc-AO__edit-actions">
                            <button type="button" className="order-doc-AO__ghost" disabled={!dirty || !!busy} onClick={() => { setDraft(draftFrom(data.form, data.document)); setDirty(false); }}>Отменить</button>
                            <button type="button" className="order-doc-AO__primary" disabled={!dirty || !!busy} onClick={save}>
                                {busy === "save" ? "Сохраняем…" : "Сохранить бланк"}
                            </button>
                        </div>
                    </div>
                    <div className="order-blank-AO__preview">
                        <div className="order-blank-AO__preview-bar">
                            <b>Бланк</b>
                            <span>{dirty ? "черновик — не забудьте сохранить" : "так будет напечатано"}</span>
                            <button type="button" className="order-doc-AO__link" onClick={openDraftPdf}>Открыть PDF</button>
                        </div>
                        <BlankSheetPreview officeId={officeId} doc={doc} draft={draft} onBlank={patchBlank} />
                    </div>
                </div>
            ) : null}

            {section === "edit" && draft ? (
                <div className="order-doc-AO__edit">
                    <p className="order-doc-AO__muted">
                        Заказчик, позиции, размеры, цвет и суммы берутся из заказа сами. Здесь — то, чего в заказе нет, или поправка только для бланка (пустое поле — как в заказе).
                    </p>

                    <fieldset>
                        <legend>Сроки и оплата</legend>
                        <label>
                            <span>Дата установки (если известна)</span>
                            <input type="date" value={draft.installationAt} onChange={(event) => patch(null, "installationAt", event.target.value)} />
                        </label>
                        <label>
                            <span>Предоплата, ₽</span>
                            <input inputMode="decimal" value={draft.prepaymentAmount} placeholder={doc.totals.prepayment ? String(doc.totals.prepayment) : "0"} onChange={(event) => patch(null, "prepaymentAmount", event.target.value)} />
                        </label>
                    </fieldset>

                    <fieldset>
                        <legend>Объект (только для бланка)</legend>
                        {field("overrides", "cemeteryName", "Кладбище / объект", doc.object.cemetery)}
                        {field("overrides", "sector", "Сектор", doc.object.sector)}
                        {field("overrides", "plot", "Участок / место", doc.object.plot)}
                        {field("overrides", "comment", "Комментарий в бланке", doc.comment, { multiline: true, rows: 3 })}
                    </fieldset>

                    <fieldset>
                        <legend>Что печатать</legend>
                        <label>
                            <span>Тип заказа</span>
                            <select value={layout.orderKind || "memorial"} onChange={(event) => patch("layout", "orderKind", event.target.value)}>
                                {ORDER_KINDS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </label>
                        <div className="order-doc-AO__checks is-wide">
                            {LAYOUT_OPTIONS.map(([key, label]) => (
                                <label key={key} className="order-doc-AO__check">
                                    <input type="checkbox" checked={layout[key] !== false} onChange={(event) => patch("layout", key, event.target.checked)} />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    {doc.shots.length ? (
                        <fieldset>
                            <legend>
                                Снимки 3D-сцены на отдельной странице (до {MAX_SHOTS})
                                {doc.order.salesSessionId ? (
                                    <button
                                        type="button"
                                        className="order-doc-AO__link"
                                        onClick={() => navigate(`/adminLayout?tab=calc3d&office=${officeId}&salesSession=${doc.order.salesSessionId}`)}
                                    >
                                        · открыть сцену
                                    </button>
                                ) : null}
                            </legend>
                            <div className="order-doc-AO__shots is-wide">
                                {doc.shots.map((shot) => {
                                    const selected = draft.sceneShotIds.includes(shot.id);
                                    return (
                                        <ShotThumb
                                            key={shot.id}
                                            officeId={officeId}
                                            shot={shot}
                                            selected={selected}
                                            disabled={draft.sceneShotIds.length >= MAX_SHOTS}
                                            onToggle={() => patch(null, "sceneShotIds", selected
                                                ? draft.sceneShotIds.filter((id) => id !== shot.id)
                                                : [...draft.sceneShotIds, shot.id].slice(0, MAX_SHOTS))}
                                        />
                                    );
                                })}
                            </div>
                        </fieldset>
                    ) : null}

                    <fieldset>
                        <legend>Условия этого бланка</legend>
                        <label className="is-wide">
                            <span>Одна строка — один пункт. Пусто — условия из шаблона офиса.</span>
                            <textarea rows={5} value={draft.termsText} placeholder={doc.terms} onChange={(event) => patch(null, "termsText", event.target.value)} />
                        </label>
                    </fieldset>

                    <div className="order-doc-AO__edit-actions">
                        <button type="button" className="order-doc-AO__ghost" disabled={!dirty || !!busy} onClick={() => { setDraft(draftFrom(data.form, data.document)); setDirty(false); }}>Отменить</button>
                        <button type="button" className="order-doc-AO__primary" disabled={!dirty || !!busy} onClick={save}>
                            {busy === "save" ? "Сохраняем…" : "Сохранить"}
                        </button>
                    </div>
                </div>
            ) : null}

            {section === "sign" ? (
                <div className="order-doc-AO__sign">
                    {doc.signature ? (
                        <div className={`order-doc-AO__signed ${doc.changedAfterSign ? "is-warn" : ""}`}>
                            <div>
                                <b>{doc.signature.method === "paper" ? "Бумажный бланк подписан" : "Заказчик подписал на экране"}</b>
                                <small>
                                    {doc.signature.signerName} · {dateText(doc.signature.signedAt, true)} · версия {doc.signature.version}
                                    {doc.signature.staffName ? ` · оформил ${doc.signature.staffName}` : ""}
                                </small>
                            </div>
                            <SignatureImage officeId={officeId} orderId={orderId} signature={doc.signature} version={doc.version} />
                            <button
                                type="button"
                                className="order-doc-AO__link is-danger"
                                disabled={!!busy}
                                onClick={() => {
                                    if (window.confirm("Снять подпись? Она останется в истории, бланк нужно будет подписать заново.")) {
                                        run("revoke", () => revokeSignature(officeId, orderId));
                                    }
                                }}
                            >
                                Снять подпись (руководитель)
                            </button>
                        </div>
                    ) : null}

                    {!doc.signature || doc.changedAfterSign ? (
                        <>
                            <p className="order-doc-AO__muted">
                                Покажите заказчику предпросмотр на вкладке «Бланк» (или распечатанный PDF). Он проверяет данные, ставит галочку и расписывается. Сразу сохранится PDF с подписью — два экземпляра.
                            </p>
                            <label className="order-doc-AO__agree">
                                <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
                                <span>С бланком ознакомлен, данные (ФИО, даты, текст, размеры, материал, цена) верны</span>
                            </label>
                            <label className="order-doc-AO__signer">
                                <span>Кто подписывает (ФИО)</span>
                                <input value={signerName} onChange={(event) => setSignerName(event.target.value)} />
                            </label>
                            <SignaturePad ref={padRef} disabled={!!busy} onChange={setPadEmpty} />
                            <div className="order-doc-AO__sign-actions">
                                <button type="button" className="order-doc-AO__primary" disabled={!canSign || !!busy} onClick={sign}>
                                    {busy === "sign" ? "Сохраняем подпись…" : "Подписать и сохранить PDF"}
                                </button>
                                <span className="order-doc-AO__muted">или</span>
                                <button type="button" className="order-doc-AO__ghost" disabled={!!busy} onClick={() => paperRef.current?.click()}>
                                    {busy === "paper" ? "Загружаем…" : "Загрузить фото подписанного бланка"}
                                </button>
                                <input
                                    ref={paperRef}
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/webp,application/pdf"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        event.target.value = "";
                                        if (file) run("paper", () => uploadPaperSignature(officeId, orderId, file, signerName));
                                    }}
                                />
                            </div>
                            <p className="order-doc-AO__muted">
                                Подпись на экране подтверждает, что заказчик ознакомился с бланком. Самый надёжный вариант — бумажный экземпляр с подписью: его фото тоже можно сохранить здесь.
                            </p>
                        </>
                    ) : null}

                    {data.signatureHistory.length ? (
                        <div className="order-doc-AO__history">
                            <h4>Прежние подписи</h4>
                            {data.signatureHistory.map((item) => (
                                <div key={item.id}>
                                    <b>{item.signerName || "—"}</b>
                                    <span>{item.method === "paper" ? "бумага" : "экран"} · версия {item.version}</span>
                                    <small>{dateText(item.signedAt, true)}{item.revokedAt ? ` · заменена ${dateText(item.revokedAt, true)}` : ""}</small>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {section === "files" ? (
                <div className="order-doc-AO__files">
                    {!data.pdfs.length ? (
                        <p className="order-doc-AO__muted">PDF ещё не делали. Нажмите «PDF · 2 экземпляра» — в файле будет экземпляр заказчика и экземпляр исполнителя.</p>
                    ) : null}
                    {data.pdfs.map((pdf) => (
                        <div key={pdf.pdfId} className="order-doc-AO__file">
                            <div>
                                <b>{pdf.title || "Бланк заказа"} · версия {pdf.version}</b>
                                <small>
                                    {dateText(pdf.createdAt, true)}{pdf.createdByName ? ` · ${pdf.createdByName}` : ""}
                                    {pdf.contentHash && pdf.contentHash !== doc.contentHash ? " · устарел (заказ менялся)" : ""}
                                </small>
                            </div>
                            {pdf.signed ? <span className="order-doc-AO__status is-ok">С подписью</span> : null}
                            <button
                                type="button"
                                className="order-doc-AO__ghost"
                                onClick={() => openInNewTab(() => fetchDocumentPdfBlob(officeId, orderId, pdf.pdfId), "blank.pdf").catch((requestError) => setError(getApiError(requestError)))}
                            >
                                Открыть
                            </button>
                            <button
                                type="button"
                                className="order-doc-AO__ghost"
                                onClick={async () => {
                                    try {
                                        openBlob(await fetchDocumentPdfBlob(officeId, orderId, pdf.pdfId), `blank-${doc.order.number}-v${pdf.version}.pdf`, true);
                                    } catch (requestError) {
                                        setError(getApiError(requestError));
                                    }
                                }}
                            >
                                Скачать
                            </button>
                            <button
                                type="button"
                                className="order-doc-AO__link is-danger"
                                disabled={!!busy}
                                onClick={() => {
                                    if (window.confirm("Удалить этот PDF?")) run("delete", () => deleteDocumentPdf(officeId, orderId, pdf.pdfId));
                                }}
                            >
                                Удалить
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
